import { useState, useEffect } from 'preact/hooks';
import Router from 'preact-router';
import { AuthProvider, useAuth } from './lib/auth';
import { I18nProvider } from './i18n/I18nContext';
import { BentoTopNav } from './components/BentoTopNav';
import { TrialBanner } from './components/TrialBanner';
import { OnboardingTour } from './components/OnboardingTour';
import { markTourComplete } from './lib/api';
import { Dashboard } from './pages/Dashboard';
import { TeamPage } from './pages/Team';
import { RequestsPage } from './pages/Requests';
import { CalendarPage } from './pages/Calendar';
import { WallchartPage } from './pages/Wallchart';
import { SettingsPage } from './pages/Settings';
import { LoginPage } from './pages/Login';
import { SetupPage } from './pages/Setup';
import { SubscriptionExpired } from './pages/SubscriptionExpired';
import { SubscriptionSuccess } from './pages/SubscriptionSuccess';
import { SubscriptionCancelled } from './pages/SubscriptionCancelled';
import { UpgradePage } from './pages/Upgrade';
import { BillingPage } from './pages/Billing';
import { CheckoutPage } from './pages/Checkout';
import { AuditTrailPage } from './pages/AuditTrail';
import { HelpPage } from './pages/Help';
import { ProfilePage } from './pages/Profile';
import { SuperAdminDashboard } from './pages/super-admin/Dashboard';
import { SuperAdminWorkspaces } from './pages/super-admin/Workspaces';
import { SuperAdminWorkspaceDetail } from './pages/super-admin/WorkspaceDetail';
import { SuperAdminPlans } from './pages/super-admin/Plans';
import { SuperAdminFeatures } from './pages/super-admin/Features';
import { SuperAdminSettings } from './pages/super-admin/Settings';
import { WorkspaceNotFoundPage } from './pages/WorkspaceNotFound';
import { InvalidSubdomainPage } from './pages/InvalidSubdomain';

const APP_DOMAIN = import.meta.env.VITE_APP_DOMAIN || 'unplug-app.com';

// Check if we're on the admin subdomain
function isAdminSubdomain() {
  if (typeof window === 'undefined') return false;
  const hostname = window.location.hostname;
  return hostname === `admin.${APP_DOMAIN}`;
}

function AppContent() {
  const { user, workspace, subscription, loading, logout, error, refreshSession, updateUser } = useAuth();
  const [currentPath, setCurrentPath] = useState(typeof window !== 'undefined' ? window.location.pathname : '/');
  const [showTour, setShowTour] = useState(false);

  // Handle route changes
  function handleRouteChange(e) {
    setCurrentPath(e.url || '/');
  }

  // Auto-trigger tour for admins who haven't completed it
  useEffect(() => {
    if (user && !user.tour_completed_at && (user.role === 'owner' || user.role === 'admin')) {
      // Small delay to let the page render first
      const timer = setTimeout(() => setShowTour(true), 500);
      return () => clearTimeout(timer);
    }
  }, [user]);

  // Handle tour completion
  async function handleTourComplete() {
    try {
      const result = await markTourComplete();
      // Optimistically update user state so tour doesn't re-trigger
      if (updateUser) {
        updateUser({ tour_completed_at: result?.tour_completed_at || new Date().toISOString() });
      }
    } catch (err) {
      console.error('Failed to mark tour complete:', err);
    }
    setShowTour(false);
  }

  // Handle tour skip - persist as completion so it doesn't reappear on reload
  async function handleTourSkip() {
    setShowTour(false);
    try {
      const result = await markTourComplete();
      if (updateUser) {
        updateUser({ tour_completed_at: result?.tour_completed_at || new Date().toISOString() });
      }
    } catch (err) {
      console.error('Failed to persist tour skip:', err);
    }
  }

  // Admin subdomain goes directly to super-admin (protected by Cloudflare Access)
  if (isAdminSubdomain()) {
    return (
      <div class="super-admin-layout">
        <Router>
          <SuperAdminDashboard path="/" />
          <SuperAdminDashboard path="/super-admin" />
          <SuperAdminWorkspaces path="/super-admin/workspaces" />
          <SuperAdminWorkspaces path="/workspaces" />
          <SuperAdminWorkspaceDetail path="/super-admin/workspaces/:id" />
          <SuperAdminWorkspaceDetail path="/workspaces/:id" />
          <SuperAdminPlans path="/super-admin/plans" />
          <SuperAdminPlans path="/plans" />
          <SuperAdminFeatures path="/super-admin/features" />
          <SuperAdminFeatures path="/features" />
          <SuperAdminSettings path="/super-admin/settings" />
          <SuperAdminSettings path="/settings" />
        </Router>
      </div>
    );
  }

  if (loading) {
    return (
      <div class="loading-screen">
        <div class="spinner" />
        <p>Loading...</p>
      </div>
    );
  }

  // Handle subdomain/workspace errors
  if (error === 'invalid_subdomain') {
    return <InvalidSubdomainPage />;
  }

  if (error === 'workspace_not_found') {
    return <WorkspaceNotFoundPage />;
  }

  if (error === 'subscription_expired') {
    return <SubscriptionExpired subscription={subscription} workspace={workspace} />;
  }

  if (error === 'initialization_failed') {
    return (
      <div class="loading-screen">
        <div class="error-icon">⚠️</div>
        <p>Failed to connect to the server.</p>
        <p class="error-detail">Please check your internet connection and try again.</p>
        <button
          class="btn btn-primary"
          onClick={() => window.location.reload()}
          style={{ marginTop: '1rem' }}
        >
          Try Again
        </button>
      </div>
    );
  }

  // Check if we're on setup page
  if (typeof window !== 'undefined' && window.location.pathname === '/setup') {
    return <SetupPage />;
  }

  // Check if we're on login page with a code/token (seamless Slack login).
  // Force LoginPage so the credential is always processed, even if a stale or
  // different session already exists in this browser.
  if (typeof window !== 'undefined') {
    const params = new URLSearchParams(window.location.search);
    if (window.location.pathname === '/login' && (params.get('code') || params.get('token'))) {
      return <LoginPage />;
    }
  }

  // Subscription result pages (no layout needed)
  if (typeof window !== 'undefined') {
    if (window.location.pathname === '/subscription/success') {
      return <SubscriptionSuccess />;
    }
    if (window.location.pathname === '/subscription/cancelled') {
      return <SubscriptionCancelled />;
    }
  }

  // Super Admin pages (protected by Cloudflare Access)
  if (typeof window !== 'undefined' && window.location.pathname.startsWith('/super-admin')) {
    return (
      <div class="super-admin-layout">
        <Router>
          <SuperAdminDashboard path="/super-admin" />
          <SuperAdminWorkspaces path="/super-admin/workspaces" />
          <SuperAdminWorkspaceDetail path="/super-admin/workspaces/:id" />
          <SuperAdminPlans path="/super-admin/plans" />
          <SuperAdminFeatures path="/super-admin/features" />
        </Router>
      </div>
    );
  }

  if (!user || !workspace) {
    return <LoginPage />;
  }

  // Check subscription status - redirect to expired page if not valid
  if (subscription && !subscription.is_valid) {
    return <SubscriptionExpired subscription={subscription} workspace={workspace} />;
  }

  // All pages use bento layout
  return (
    <div class="bento-dashboard-layout">
      <OnboardingTour
        isOpen={showTour}
        onComplete={handleTourComplete}
        onSkip={handleTourSkip}
      />
      <BentoTopNav
        user={user}
        workspace={workspace}
        onLogout={logout}
        currentPath={currentPath}
        onRestartTour={() => setShowTour(true)}
      />
      <TrialBanner subscription={subscription} />
      <main class="bento-page-container">
        <Router onChange={handleRouteChange}>
          <Dashboard path="/" user={user} workspace={workspace} subscription={subscription} onLogout={logout} />
          <WallchartPage path="/wallchart" user={user} workspace={workspace} />
          <TeamPage path="/team" user={user} workspace={workspace} />
          <RequestsPage path="/requests" user={user} workspace={workspace} />
          <CalendarPage path="/calendar" user={user} workspace={workspace} />
          <SettingsPage path="/settings" user={user} workspace={workspace} />
          <AuditTrailPage path="/audit-trail" user={user} workspace={workspace} />
          <UpgradePage path="/upgrade" user={user} workspace={workspace} />
          <CheckoutPage path="/checkout" user={user} workspace={workspace} />
          <BillingPage path="/billing" user={user} workspace={workspace} />
          <HelpPage path="/help" user={user} workspace={workspace} onRestartTour={() => setShowTour(true)} />
          <ProfilePage path="/profile" user={user} workspace={workspace} />
        </Router>
      </main>
    </div>
  );
}

export function App() {
  return (
    <I18nProvider>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </I18nProvider>
  );
}
